import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { select, Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, SelectOption } from '@fcom/common/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';
import { Category, FinnairItineraryItemFlight, FinnairServiceItem } from '@fcom/dapi/api/models';
import { IconButtonSize, IconButtonTheme } from '@fcom/ui-components';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { SeatMapTraveler } from '@fcom/common-booking/modules/ancillaries/interfaces';
import { TravelerService } from '@fcom/dapi/interfaces';
import { selectEnableRemovingSeatSelection } from '@fcom/common-booking/store/selectors/ui-action.selector';
import { CommonBookingFeatureState, SeatMapActions, ServicesActions } from '@fcom/common-booking/store';

import { getSeatMapFlight } from '../../../utils';

@Component({
  selector: 'fin-seat-passengers',
  templateUrl: './seat-passengers.component.html',
  styleUrls: ['./seat-passengers.component.scss'],
  standalone: false,
})
export class SeatPassengersComponent implements OnInit, OnChanges, OnDestroy {
  private formBuilder = inject(FormBuilder);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private store$ = inject<Store<CommonBookingFeatureState>>(Store);
  protected getIconForServiceCategory = getIconForServiceCategory;
  protected shouldShowRemoveButtonByPax$: Observable<Record<string, boolean>>;

  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() selectedSeatsByPax$: Observable<Record<string, string>>;
  @Input() selectedBundleCategoriesByPax$: Observable<Record<string, Category[]>>;
  @Input() flight: FinnairItineraryItemFlight;
  @Input() includedSeats: FinnairServiceItem | null;
  @Input() contextCategory: Category;
  @Input() activeBundlesByPax: Record<string, TravelerService[]>;
  @Input() activePax: SeatMapTraveler;

  @Output() selectPax: EventEmitter<string> = new EventEmitter();

  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  passengerForm: FormGroup;
  options$: Observable<SelectOption[]>;
  subscriptions: Subscription = new Subscription();

  ngOnInit(): void {
    this.createForm();
    this.passengerForm = this.formBuilder.group({
      activeTravelerId: new FormControl(null),
    });

    this.shouldShowRemoveButtonByPax$ = this.store$.pipe(
      select(
        selectEnableRemovingSeatSelection({
          flightId: this.flight.id,
          contextCategory: this.contextCategory,
        })
      )
    );

    this.subscriptions.add(
      this.passengerForm
        .get('activeTravelerId')
        .valueChanges.pipe(distinctUntilChanged(), withLatestFrom(this.passengers$))
        .subscribe(([activeTravelerId, _passengers]) => {
          if (activeTravelerId !== this.activePax.id) {
            this.selectPax.emit(activeTravelerId);
          }
        })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.passengers || !this.passengerForm) {
      return;
    }
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  createForm(): void {
    this.options$ = combineLatest([
      this.languageService.translate('seatMap.and'),
      this.passengers$,
      this.selectedSeatsByPax$,
      this.selectedBundleCategoriesByPax$,
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(([_withInfantLabel, _passengers]) => {
        this.passengerForm.get('activeTravelerId').setValue(this.activePax.id);
      }),
      switchMap(([withInfantLabel, passengers, selectedSeats, selectedBundleCategories]) => {
        const bundleTranslations$ = passengers.some((p) => selectedBundleCategories[p.id]?.length > 0)
          ? combineLatest(
              Array.from(
                new Set(passengers.map((p) => selectedBundleCategories[p.id]).find((cat) => cat.length > 0))
              ).map((category) =>
                this.languageService
                  .translate(`ancillaries.${category}.extraTitle`)
                  .pipe(map((translation) => ({ category, translation })))
              )
            )
          : of([]);

        return bundleTranslations$.pipe(
          map((bundleTranslations) => {
            return passengers.map((passenger) => {
              const paxName = `${passenger.traveler.firstName} ${passenger.traveler.lastName}`;
              const paxNameWithInfant = `${paxName} ${withInfantLabel} ${passenger.withInfantFullName}`;
              const seatNumber = selectedSeats[passenger.id] ? `, ${selectedSeats[passenger.id]}` : '';
              const bundleTitle = this.activeBundlesByPax?.[passenger.id]?.[0].title
                ? ` + ${bundleTranslations.find((bt) => bt.category === this.activeBundlesByPax[passenger.id]?.[0].title)?.translation || ''}`
                : '';

              return {
                name: `${passenger.withInfant ? paxNameWithInfant : paxName}${seatNumber}${bundleTitle}`,
                value: passenger.id,
              };
            });
          })
        );
      })
    );
  }

  handleClick(passengerId: string, index: number): void {
    this.selectPax.emit(passengerId);

    this.gtmService.seatMapEvent(`pax-select-${index + 1}`, ElementTypes.BUTTON, '', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  clearSelection(passengerId: string): void {
    this.selectedBundleCategoriesByPax$.pipe(take(1)).subscribe((selectedBundlesByPax) => {
      if (selectedBundlesByPax?.[passengerId]?.length > 0) {
        this.store$.dispatch(
          ServicesActions.setBundleSelectionForFragmentForTraveler({
            fragmentId: this.flight.id,
            travelerId: passengerId,
            selection: undefined,
          })
        );
      } else {
        this.store$.dispatch(
          SeatMapActions.setSeatForFlight({
            flightId: this.flight.id,
            travelerId: passengerId,
            seat: undefined,
          })
        );
      }
    });
  }
}
